import React, { useState, useEffect } from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { ContactSupportDialog } from "./ContactSupportDialog";
import { getUserFacilities } from "@services/facilityManagement";
import { getCurrentUserId } from "@utils/userUtils";
import {
  getUserPreferredFacilities,
  UserPreferredFacility,
} from "@services/payment";

/* ── Styled components (matching Profile page CSS) ── */

const PageContainer = styled(Box)({
  background: "#fdffff",
  borderRadius: "12px",
  height: "97vh",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #99d4e1",
  boxShadow: "8px 2px 58.4px 0px inset rgba(191, 242, 255, 0.2)",
  position: "relative",
  overflow: "hidden",
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  padding: "12px 64px",
  height: "124px",
  display: "flex",
  alignItems: "end",
  gap: "8px",
  position: "sticky",
  top: 0,
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    padding: "12px 24px",
    height: "96px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
    height: "80px",
  },
}));

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "28px",
  fontWeight: 500,
  lineHeight: "1.5",
  color: "#262626",
  letterSpacing: "-1px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "32px 48px 16px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  overflowY: "auto",
  overflowX: "hidden",
  [theme.breakpoints.down("md")]: {
    padding: "24px 24px 16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 16px",
  },
}));

/* ── Component ── */

interface VerificationFailedPageProps {
  /** Called when user clicks OK – parent can navigate away */
  onOk?: () => void;
}

const VerificationFailedPage: React.FC<VerificationFailedPageProps> = ({
  onOk,
}) => {
  const location = useLocation();
  const routeState = location.state as {
    selectedFacility?: {
      facility_name?: string;
      city?: string;
      state?: string;
      ccn_number?: string;
      facility_type?: string;
    };
  };
  const [contactSupportOpen, setContactSupportOpen] = useState(false);
  const [hasExistingFacilities, setHasExistingFacilities] = useState(false);
  const [failedFacilities, setFailedFacilities] = useState<
    UserPreferredFacility[]
  >([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = getCurrentUserId();
        if (userId === "0") return;
        const numericUserId = parseInt(userId);

        // Always fetch user facilities to check for existing facilities
        const userFacilitiesRes = await getUserFacilities(numericUserId);
        const userFacs = userFacilitiesRes.facilities || [];
        setHasExistingFacilities(userFacs.length > 0);

        // If selectedFacility is passed via navigation state, use only that
        if (routeState?.selectedFacility) {
          const selected = routeState.selectedFacility;
          const mockFacility: UserPreferredFacility = {
            id: 0,
            user_id: numericUserId,
            facility_details: {
              facility_name: selected.facility_name || "",
              city: selected.city || "",
              state: selected.state || "",
              ccn_number: selected.ccn_number || "",
              facility_type: selected.facility_type || "",
            },
            facility_name: selected.facility_name || "",
            city: selected.city || "",
            state: selected.state || "",
            ccn_number: selected.ccn_number || "",
            facility_type: selected.facility_type || "",
            is_verified: false,
            is_active: false,
            is_delete: false,
          } as UserPreferredFacility;
          setFailedFacilities([mockFacility]);
        } else {
          // Otherwise, fetch all failed/inactive facilities
          const preferredRes = await getUserPreferredFacilities(1, 20, numericUserId);
          const preferred =
            preferredRes.result?.data?.filter((f) => !f.is_delete) || [];

          setHasExistingFacilities(userFacs.length > 0 || preferred.length > 0);
          setFailedFacilities(
            preferred.filter((f) => !f.is_verified && f.status !== "Pending")
          );
        }
      } catch {
        // ignore
      }
    };
    fetchData();
  }, [routeState?.selectedFacility]);

  return (
    <PageContainer>
      {/* ── Header ── */}
      <HeaderContainer>
        <HeaderTitle>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "999px",
              backgroundColor: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ErrorOutlineOutlined sx={{ fontSize: 22, color: "#DC2626" }} />
          </Box>
          <Title>Facility Verification Not Approved</Title>
        </HeaderTitle>
      </HeaderContainer>

      {/* ── Content ── */}
      <ContentSection>
        <Box sx={{ maxWidth: 720, width: "100%" }}>
          {/* Failed facilities list */}
          {failedFacilities.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "#065B70",
                  mb: 1.5,
                }}
              >
                Selected Facility
              </Typography>
              {failedFacilities.map((f) => (
                <Box
                  key={f.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                    px: 2,
                    mb: 1,
                    borderRadius: "8px",
                    border: "1px solid #e5e5e5",
                    background: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#DC2626",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "14px",
                      color: "#262626",
                    }}
                  >
                    {f.facility_name ||
                      f.facility_details?.facility_name ||
                      "Unknown Facility"}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "12px",
                      color: "#DC2626",
                      ml: "auto",
                    }}
                  >
                    Verification failed
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Main message card */}
          <Box
            sx={{
              background: "#FFF5F5",
              border: "1px solid #FECACA",
              borderRadius: "12px",
              p: 4,
              mt: 3,
            }}
          >
            <Typography
              sx={{
                color: "#262626",
                fontSize: "15px",
                fontFamily: '"Rethink Sans", sans-serif',
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              <p>We have completed the review of your selected facility and were unable to verify the information provided.</p>
              
              <p>At this time, access to the facility cannot be granted. This may be due to one of the following reasons:</p>
              <p>• The facility information could not be confirmed </p>
              <p>• The facility is already associated with another account</p>
              <p>• Additional documentation is required to validate your association</p>
              
              <p>If you believe this is an error or would like to proceed, please reply to the verification email with any supporting details or documentation that can help us verify your request.</p>
              <p>Your facility will remain in a pending state for 48 hours while we allow time for additional information to be submitted.</p>
              
            </Typography>

            {hasExistingFacilities && (
              <Typography
                sx={{
                  color: "#4b5563",
                  fontSize: "13px",
                  fontFamily: '"Rethink Sans", sans-serif',
                  lineHeight: 1.6,
                }}
              >
                You can continue using the platform for your existing verified
                facilities while this issue is being resolved.
              </Typography>
            )}
          </Box>

          {/* Action buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              onClick={() => setContactSupportOpen(true)}
              sx={{
                background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
                boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
                borderRadius: "8px",
                padding: "10px 24px",
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                fontSize: "14px",
                color: "#e6fafc",
                textTransform: "none",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #086e86 0%, #4ab3c7 100%)",
                },
              }}
            >
              Contact Support
            </Button>
          </Box>
        </Box>
      </ContentSection>

      {/* Contact Support Dialog */}
      <ContactSupportDialog
        open={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
        onSuccess={() => {
          toast.success("Your message or your query has been sent successfully.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }}
      />
    </PageContainer>
  );
};

export default VerificationFailedPage;
